import type { PublicAuditRun } from "@/lib/audit.functions";

/**
 * Neutral trust-seal badge shown on customer sites. Never surfaces raw
 * store_score. When aeoScore >= 80 we upgrade to the "AI-Readable Store"
 * variant tied to our GEO positioning; otherwise a plain "Audited by
 * Shugert" verified seal.
 */
export function AuditBadge({
  run,
  href,
  asLink = true,
}: {
  run: Pick<PublicAuditRun, "slug" | "hostname" | "aeoScore">;
  href?: string;
  asLink?: boolean;
}) {
  const isAiReady = (run.aeoScore ?? 0) >= 80;
  const accent = isAiReady ? "#10b981" : "#0a0a0a";
  const eyebrow = isAiReady ? "AI-Readable Store" : "Audited by";
  const line = isAiReady ? "Verified by Shugert" : "Shugert";
  const target = href ?? `https://shugert.com.mx/audit/results/${run.slug}`;

  const inner = (
    <>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          background: `${accent}14`,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        aria-hidden
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2.5l2.39 1.74 2.95-.11.9 2.81 2.37 1.77-1.04 2.77 1.04 2.77-2.37 1.77-.9 2.81-2.95-.11L12 21.5l-2.39-1.74-2.95.11-.9-2.81-2.37-1.77 1.04-2.77-1.04-2.77 2.37-1.77.9-2.81 2.95.11L12 2.5z"
            fill={accent}
            fillOpacity="0.18"
            stroke={accent}
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <path
            d="M8.5 12.2l2.4 2.4 4.6-4.9"
            stroke={accent}
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </div>
      <div style={{ lineHeight: 1.2, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "#64748b",
            fontWeight: 600,
          }}
        >
          {eyebrow}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0a0a0a", marginTop: 2 }}>{line}</div>
        <div
          style={{
            fontSize: 11,
            color: "#64748b",
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {run.hostname}
        </div>
      </div>
    </>
  );

  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    textDecoration: "none",
    color: "#0a0a0a",
    background: "#fff",
    width: 244,
    boxSizing: "border-box",
  };

  if (!asLink) return <div style={style}>{inner}</div>;
  return (
    <a href={target} target="_blank" rel="noopener noreferrer" style={style}>
      {inner}
    </a>
  );
}
